import { makeAutoObservable, runInAction } from "mobx";
import { ApiError, setAuthToken } from "@/clients/ApiClient";
import { createClient } from "@/core/helpers";

const TOKEN_KEY = "hp_auth_token";

export type AuthStatus = "unknown" | "anonymous" | "authenticated";
export type ProfileError = "invalid_name" | "invalid_podcasts" | "network";
export type SignInError = "invalid_email" | "rate_limited" | "invalid_or_expired_code" | "send_failed" | "network";

const readToken = (): string | null => {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
};

const writeToken = (token: string | null) => {
    try {
        if (token) {
            localStorage.setItem(TOKEN_KEY, token);
        } else {
            localStorage.removeItem(TOKEN_KEY);
        }
    } catch {
        // Storage can be blocked; the session then only lasts until the page is closed
    }
};

const errorCode = (error: unknown): SignInError => {
    if (error instanceof ApiError) {
        const code = (error.body as { error?: string } | null)?.error;
        if (code === "invalid_email" || code === "rate_limited" || code === "invalid_or_expired_code") {
            return code;
        }
        if (error.status === 429) return "rate_limited";
        if (error.status === 503) return "send_failed";
    }
    return "network";
};

const profileErrorCode = (error: unknown): ProfileError => {
    const code = error instanceof ApiError ? (error.body as { error?: string } | null)?.error : undefined;
    return code === "invalid_name" || code === "invalid_podcasts" ? code : "network";
};

/**
 * Who is signed in, and which podcasts they own. Sign-in is a two step email code flow:
 * `requestCode(email)` then `verifyCode(code)`.
 */
export class AuthStore {
    status: AuthStatus = "unknown";
    user: AuthUser | null = null;
    podcasts: PodcastRef[] = [];
    /** The podcasts the user likes */
    favourites: PodcastRef[] = [];
    profileError: ProfileError | null = null;
    /** Set when the first-sign-in questions were closed without reaching the server, to not ask again this visit */
    profileDismissed = false;

    /** The address a code was sent to; set between the two steps */
    pendingEmail: string | null = null;
    /** Seconds the server asked us to wait before another code can be requested */
    retryAfter = 0;
    error: SignInError | null = null;
    busy = false;

    private client = createClient();

    constructor() {
        makeAutoObservable<AuthStore, "client">(this, { client: false });
    }

    get isSignedIn() {
        return this.status === "authenticated";
    }

    /** True for a new account that has neither answered nor skipped the first-sign-in questions */
    get needsProfile() {
        return this.isSignedIn && this.user !== null && !this.user.onboarded && !this.profileDismissed;
    }

    /** Call once on startup: validates a stored token and loads the user */
    async restore() {
        const token = readToken();
        if (!token) {
            this.status = "anonymous";
            return;
        }

        setAuthToken(token);
        try {
            const me = await this.client.get<MeResponse>("/auth/me");
            runInAction(() => this.applyMe(me));
        } catch (error) {
            // Only a rejected token ends the session; a network blip should not sign people out
            if (error instanceof ApiError && error.status === 401) {
                this.clearSession();
            } else {
                runInAction(() => {
                    this.status = "anonymous";
                });
            }
        }
    }

    async requestCode(email: string): Promise<boolean> {
        this.busy = true;
        this.error = null;
        try {
            await this.client.post("/auth/request-code", { email });
            runInAction(() => {
                this.pendingEmail = email.trim();
                this.retryAfter = 0;
            });
            return true;
        } catch (error) {
            runInAction(() => {
                this.error = errorCode(error);
                if (error instanceof ApiError && error.status === 429) {
                    this.retryAfter = Number((error.body as { retry_after?: number } | null)?.retry_after ?? 0);
                }
            });
            return false;
        } finally {
            runInAction(() => {
                this.busy = false;
            });
        }
    }

    async verifyCode(code: string): Promise<boolean> {
        if (!this.pendingEmail) return false;

        this.busy = true;
        this.error = null;
        try {
            const result = await this.client.post<SignInResponse>("/auth/verify", {
                email: this.pendingEmail,
                code,
            });
            writeToken(result.token);
            setAuthToken(result.token);
            const me = await this.client.get<MeResponse>("/auth/me");
            runInAction(() => {
                this.applyMe(me);
                this.pendingEmail = null;
            });
            return true;
        } catch (error) {
            runInAction(() => {
                this.error = errorCode(error);
            });
            return false;
        } finally {
            runInAction(() => {
                this.busy = false;
            });
        }
    }

    /**
     * Save the first-sign-in answers. Leaving both fields out records that the user skipped, so
     * they are not asked again.
     */
    async saveProfile(input: { name?: string; podcastIds?: number[] } = {}): Promise<boolean> {
        this.busy = true;
        this.profileError = null;
        try {
            const result = await this.client.post<ProfileResponse>("/auth/profile", {
                name: input.name,
                podcast_ids: input.podcastIds,
            });
            runInAction(() => {
                this.user = result.user;
                this.favourites = result.favourites;
            });
            return true;
        } catch (error) {
            runInAction(() => {
                this.profileError = profileErrorCode(error);
            });
            return false;
        } finally {
            runInAction(() => {
                this.busy = false;
            });
        }
    }

    /** Close the questions without answering; if the server cannot be reached, just stop asking for now */
    async skipProfile() {
        if (!(await this.saveProfile())) {
            runInAction(() => {
                this.profileDismissed = true;
                this.profileError = null;
            });
        }
    }

    /** Skip to the code step for an address that already has a code, e.g. after a reload */
    enterCode(email: string) {
        this.pendingEmail = email.trim();
        this.error = null;
    }

    /** Back to the email step, e.g. to correct a typo */
    changeEmail() {
        this.pendingEmail = null;
        this.error = null;
    }

    async logout() {
        try {
            await this.client.post("/auth/logout");
        } catch (error) {
            // The token is dropped locally either way
            console.error("Failed to revoke session:", error);
        }
        this.clearSession();
    }

    private applyMe(me: MeResponse) {
        this.user = me.user;
        this.podcasts = me.podcasts;
        this.favourites = me.favourites;
        this.status = "authenticated";
    }

    private clearSession() {
        writeToken(null);
        setAuthToken(null);
        this.user = null;
        this.podcasts = [];
        this.favourites = [];
        this.profileError = null;
        this.profileDismissed = false;
        this.status = "anonymous";
    }
}
